import { useState } from "react";
import { Layout } from "@layouts";
import { Card, PageBreadcrumbs } from "@molecules";
import { Bar } from "@organisms";
import { LSACategory, PagePaths } from "../../../../types";
import { Check, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
//import { lifestyleCategories } from "./categories-data";

export type LifestyleCategoriesPageTemplateProps = {
  navigateToPage: (to: string) => void;
  onReturnPressed: () => void;
  selectedCategories: LSACategory[];
  onToggleCategory: (category: LSACategory) => void;
  onContinue: () => void;
  lifestyleGroupName: string;
  onLifestyleGroupNameChange: (name: string) => void;
  lifestyleCategories: LSACategory[];
  isLoadingCategories: boolean;
  onSelectAllCategories: () => void;
};

export const LifestyleCategoriesPageTemplate = ({
  navigateToPage,
  onReturnPressed,
  selectedCategories,
  onToggleCategory,
  onContinue,
  lifestyleGroupName,
  onLifestyleGroupNameChange,
  lifestyleCategories,
  isLoadingCategories,
  onSelectAllCategories
}: LifestyleCategoriesPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const goToPerks = () => navigateToPage(`/dashboard/${PagePaths.perks}`);
  const goToLifestyleMain = () => navigateToPage(`/dashboard/${PagePaths.lifestyle}/main`);
  const hasGroupName = Boolean(lifestyleGroupName?.trim());
  const hasSelectedCategories = selectedCategories.length > 0;
  const [infoPopoverCategoryId, setInfoPopoverCategoryId] = useState<string | null>(null);
  const areAllCategoriesSelected = lifestyleCategories.length > 0 && selectedCategories.length === lifestyleCategories.length;
  const isSelectAllDisabled = isLoadingCategories || lifestyleCategories.length === 0 || areAllCategoriesSelected;

  const getCategoryContent = (category: LSACategory) => {
    const lookupCategoryDetails = (baseKey: string) => {
      const value = t(`${baseKey}.${category.id}`, {
        returnObjects: true,
        defaultValue: null,
      }) as { name?: string; description?: string; examples?: string[] } | null;
      return typeof value === "string" ? null : value;
    };

    const translated =
      lookupCategoryDetails("lifestyleCategories.categoryDetails") ||
      lookupCategoryDetails("lifestyleManageGroup.categories.categoryDetails");

    const localizedExamples = Array.isArray(translated?.examples)
      ? translated.examples
      : Array.isArray((category as any)?.examples)
        ? (category as any).examples
        : [];

    return {
      name: translated?.name || (category as any).name,
      description: translated?.description || (category as any).description,
      examples: localizedExamples,
    };
  };

  const handleInfoMouseEnter = (event: React.MouseEvent, categoryId: string) => {
    event.stopPropagation();
    setInfoPopoverCategoryId(categoryId);
  };

  const handleInfoMouseLeave = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setInfoPopoverCategoryId(null);
  };

  const isCategoryInfoVisible = (categoryId: string) => infoPopoverCategoryId === categoryId;

  return (
    <Layout
      variant="dashboard"
      header={t("lifestyleCategories.header")}
      subheader={
        <div className="flex flex-row justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("lifestyleCategories.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("lifestyleCategories.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("lifestyleCategories.breadcrumbs.lifestyle"), onClick: goToLifestyleMain },
            { label: t("lifestyleCategories.breadcrumbs.createGroup") },
          ]}
        />
        <div className="flex flex-col pb-8">
          {/* Numbered Title */}
          <div className="mb-4 mt-2">
            <h2 className="text-neutral-darkest text-xl font-normal mb-2">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  1.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("lifestyleCategories.step1Title")}
                  {/* {hasGroupName ? <Check className="w-7 h-7 text-green-500" /> : null} */}
                </span>
              </div>
            </h2>
            <p className="text-gray-600 mb-6">
              {t("lifestyleCategories.step1Description")}
            </p>
            
            {/* Lifestyle Group Name Input */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("lifestyleCategories.groupNameLabel")}
              </label>
              <input
                type="text"
                value={lifestyleGroupName}
                onChange={(e) => onLifestyleGroupNameChange(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-mediumish focus:border-transparent"
                placeholder={t("lifestyleCategories.groupNamePlaceholder")}
              />
              <p className="text-xs text-gray-500 mt-1">{t("lifestyleCategories.groupNameHelper")}</p>
            </div>
          </div>

          {/* Categories Section */}
          <div className="mb-2">
            <h3 className="text-neutral-darkest text-xl font-normal mb-2">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  2.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("lifestyleCategories.step2Title")}
                  {/* {hasSelectedCategories ? <Check className="w-7 h-7 text-green-500" /> : null} */}
                </span>
              </div>
            </h3>
            <div className="flex items-start justify-between gap-4 mb-6 w-full">
              <p className="text-gray-600 flex-1">
                {t("lifestyleCategories.step2Description")}
              </p>
              <button
                type="button"
                onClick={onSelectAllCategories}
                disabled={isSelectAllDisabled}
                className="text-sm font-normal text-brand-orange hover:text-primary-dark transition-colors inline-flex items-center justify-center ml-4 mr-2 pr-[15px] pl-0 bg-transparent border-none disabled:text-neutral-medium disabled:cursor-not-allowed whitespace-nowrap self-start"
              >
                {t("lifestyleCategories.selectAll")}
              </button>
            </div>
            {isLoadingCategories && (
              <div className="text-gray-600 mb-6">
                {t("lifestyleCategories.loading")}
              </div>
            )}
          </div>

          {/* Categories Grid */}
          <div
            className="grid grid-cols-1 min-[600px]:grid-cols-2 xl:grid-cols-3 gap-4 mb-8 w-full"
            style={{ maxWidth: "1600px" }}
          >
            {lifestyleCategories.map((category) => {
              const isSelected = selectedCategories.some((selected) => selected.id === category.id);
              const { name, description, examples } = getCategoryContent(category);
              const categoryExamples = Array.isArray(examples) ? examples : [];
              return (
                <div
                  key={category.id}
                  className={`flex items-center justify-between gap-4 rounded-2xl cursor-pointer transition-all p-4 ${
                    isSelected
                      ? "border-2 border-primary-mediumish bg-white hover:border-primary-mediumish hover:bg-primary-lightest"
                      : "border-2 border-neutral-light bg-white hover:border-primary-mediumish hover:bg-primary-lightest"
                  }`}
                  onClick={() => onToggleCategory(category)}
                >
                  <div className="flex items-center gap-4 flex-1 pr-2">
                    <div className="h-12 w-12 rounded-xl border border-neutral-light flex items-center justify-center bg-neutral-lightest text-xl">
                      <span>{category.icon}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-semibold text-neutral-darkest">{name}</div>
                      <div className="text-xs text-neutral-mediumDark break-words mt-0.5">
                        {description}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-7 w-7 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? "border-primary-medium bg-primary-medium text-white"
                          : "border-neutral-light bg-white text-transparent"
                      }`}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                    <div className="relative flex-shrink-0" onMouseLeave={() => handleInfoMouseLeave()}>
                      <button
                        type="button"
                        className="p-2 rounded-full text-neutral-mediumDark hover:text-primary-medium focus:outline-none"
                        onMouseEnter={(event) => handleInfoMouseEnter(event, category.id)}
                        onMouseLeave={handleInfoMouseLeave}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Info className="h-5 w-5" />
                      </button>
                     {isCategoryInfoVisible(category.id) ? (
                       <div
                         className="absolute right-0 top-9 z-20 w-64 p-3 rounded-lg shadow-xl border border-neutral-light bg-white text-left"
                         onMouseEnter={(event) => handleInfoMouseEnter(event, category.id)}
                         onMouseLeave={handleInfoMouseLeave}
                       >
                          <p className="text-xs font-semibold text-neutral-darkest mb-2">{t("lifestyleCategories.examplesTitle")}</p>
                          {categoryExamples.length ? (
                            <ul className="list-disc list-outside pl-4 space-y-1 max-h-48 overflow-auto text-xs text-neutral-dark">
                              {categoryExamples.map((example) => (
                                <li key={`${category.id}-${example}`}>{example}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-neutral-medium">{t("lifestyleCategories.noExamples")}</p>
                          )}
                       </div>
                     ) : null}
                   </div>
                 </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={onContinue}
          isContinueDisabled={selectedCategories.length === 0 || !lifestyleGroupName.trim()}
        />
      </div>
    </Layout>
  );
}; 
